// Pure-frontend 12-month cashflow projection.
//
// Inputs are the same data the dashboard already reads — no fresh
// fetches — so the forecast stays in sync with anything the user
// edits on the page.
//
// Conventions worth flagging:
//
//  - The "current balance" is treated as start-of-current-month. The
//    PRD validation table in the seed file (section 8) follows the
//    same convention: the May 2026 row's cumulative is opening_balance
//    + may_net. If the user reconciled mid-month, the current month's
//    cumulative will overshoot reality by however much they've
//    already spent — they fix it by updating the balance entry, which
//    is the source of truth.
//
//  - Installments fire in every month from now through
//    `last_deduction_month` inclusive. We deliberately don't trust
//    `remaining_months` here because the seed's convention isn't
//    consistent with the validation table (the table fires Saudia in
//    May AND June even though remaining_months=1); `last_deduction_month`
//    is the unambiguous bound.
//
//  - Monthly special obligations (swimming, jam3eya, monthly custom)
//    fire each month between `start_date` and `end_date` inclusive,
//    matching what those date columns mean for those kinds.
//
//  - Rent (every_6_months) fires in `attribution_month` and every
//    6 months after, so the same column governs every future hit
//    within the horizon.

const RISK_TIER = (amount) => {
  if (amount < 1000) return 'critical'
  if (amount < 5000) return 'warning'
  return 'safe'
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function monthKey(year, monthOneBased) {
  return `${year}-${pad2(monthOneBased)}`
}

// '2026-05' → 2026 * 12 + 4 (May is index 4)
function monthIndex(key) {
  if (!key) return null
  const [y, m] = key.split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null
  return y * 12 + (m - 1)
}

function addMonths(key, n) {
  const idx = monthIndex(key)
  if (idx == null) return null
  const next = idx + n
  return monthKey(Math.floor(next / 12), (next % 12) + 1)
}

function dateToMonthKey(isoDate) {
  if (!isoDate || typeof isoDate !== 'string') return null
  return isoDate.slice(0, 7)
}

function currentMonthKey(today = new Date()) {
  return monthKey(today.getFullYear(), today.getMonth() + 1)
}

// --- per-month line items ---------------------------------------------------

function installmentForMonth(installment, mKey, paidSet) {
  const last = installment?.last_deduction_month
  if (!last) return 0
  const start = installment?.first_deduction_month
  const monthIdx = monthIndex(mKey)
  const lastIdx = monthIndex(last)
  if (monthIdx == null || lastIdx == null) return 0
  if (monthIdx > lastIdx) return 0
  if (start) {
    const startIdx = monthIndex(start)
    if (startIdx != null && monthIdx < startIdx) return 0
  }
  // Skip if the user has already settled this installment for this
  // cycle (a confirmed pending_finance_events row for the same key).
  if (paidSet && paidSet.has(`installment:${installment.id}:${mKey}`)) return 0
  return Number(installment.monthly_amount) || 0
}

function obligationForMonth(obligation, mKey, paidSet) {
  if (obligation?.status !== 'active') return 0
  const amount = Number(obligation.amount) || 0
  const freq = obligation.frequency
  const paid = paidSet && paidSet.has(`special_obligation:${obligation.id}:${mKey}`)

  if (freq === 'every_6_months') {
    const attr = obligation.attribution_month
    const attrIdx = monthIndex(attr)
    const mIdx = monthIndex(mKey)
    if (attrIdx == null || mIdx == null) return 0
    if (mIdx < attrIdx) return 0
    if (paid) return 0
    return ((mIdx - attrIdx) % 6 === 0) ? amount : 0
  }

  if (freq === 'monthly') {
    const startIdx = monthIndex(dateToMonthKey(obligation.start_date))
    const endIdx = monthIndex(dateToMonthKey(obligation.end_date))
    const mIdx = monthIndex(mKey)
    if (mIdx == null) return 0
    if (startIdx != null && mIdx < startIdx) return 0
    if (endIdx != null && mIdx > endIdx) return 0
    if (paid) return 0
    return amount
  }

  if (freq === 'one_time') {
    const fireKey = dateToMonthKey(obligation.start_date) || obligation.attribution_month
    if (paid) return 0
    return fireKey === mKey ? amount : 0
  }

  return 0
}

function monthlyIncomeSum(incomeSources) {
  return incomeSources
    .filter((s) => s.status === 'active' && s.frequency === 'monthly')
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0)
}

function monthlyFixedSum(fixedExpenses) {
  return fixedExpenses
    .filter((e) => e.status === 'active')
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
}

// --- main projection --------------------------------------------------------

export function computeCashflowForecast({
  currentBalance = 0,
  incomeSources = [],
  fixedExpenses = [],
  variableEstimate = 0,
  installments = [],
  specialObligations = [],
  // Set of `${source_kind}:${source_id}:${period_month}` for events
  // the user has confirmed via the dues sheet — those line items
  // disappear from the forecast for their period.
  paidEvents = null,
  horizonMonths = 12,
  today = new Date(),
}) {
  const startKey = currentMonthKey(today)
  const incomeTotal = monthlyIncomeSum(incomeSources)
  const fixedTotal = monthlyFixedSum(fixedExpenses)
  const variable = Number(variableEstimate) || 0

  const rows = []
  let cumulative = Number(currentBalance) || 0

  for (let i = 0; i < horizonMonths; i++) {
    const mKey = addMonths(startKey, i)

    const installmentItems = installments
      .filter((inst) => inst.status === 'active')
      .map((inst) => ({
        id: inst.id,
        label: inst.label ? `${inst.provider} ${inst.label}` : inst.provider,
        amount: installmentForMonth(inst, mKey, paidEvents),
        lastMonth: inst.last_deduction_month,
      }))
      .filter((x) => x.amount > 0)

    const obligationItems = specialObligations
      .filter((o) => o.status === 'active')
      .map((o) => ({
        id: o.id,
        kind: o.kind,
        label: o.name,
        amount: obligationForMonth(o, mKey, paidEvents),
      }))
      .filter((x) => x.amount > 0)

    const installmentsTotal = installmentItems.reduce((s, x) => s + x.amount, 0)
    const obligationsTotal = obligationItems.reduce((s, x) => s + x.amount, 0)

    const outflow = fixedTotal + variable + installmentsTotal + obligationsTotal
    const net = incomeTotal - outflow
    cumulative = cumulative + net

    // Per-row "what's ending this month" notes — useful for the table
    // labels ("Saudia last deduction", "Amazon last deduction", etc.).
    const lastDeductions = installmentItems
      .filter((x) => x.lastMonth === mKey)
      .map((x) => x.label)

    rows.push({
      monthKey: mKey,
      income: incomeTotal,
      fixed: fixedTotal,
      variable,
      installmentsTotal,
      installmentItems,
      obligationsTotal,
      obligationItems,
      outflow,
      net,
      cumulativeBalance: cumulative,
      risk: RISK_TIER(cumulative),
      lastDeductions,
    })
  }

  return rows
}

// Helper used by the targets section to predict when a balance
// threshold will be hit. Returns the YYYY-MM of the first row whose
// cumulative crosses the threshold, or null if it doesn't in horizon.
export function findThresholdMonth(rows, threshold) {
  if (!Array.isArray(rows) || threshold == null) return null
  for (const row of rows) {
    if (row.cumulativeBalance >= threshold) return row.monthKey
  }
  return null
}

// Find when (which month) all active installments are gone. Returns
// the YYYY-MM after the last deduction, or null if outside horizon.
export function findAllInstallmentsClearedMonth(installments) {
  const active = installments.filter((i) => i.status === 'active' && i.last_deduction_month)
  if (active.length === 0) return null
  const lastIdx = Math.max(...active.map((i) => monthIndex(i.last_deduction_month)))
  if (!Number.isFinite(lastIdx)) return null
  return monthKey(Math.floor((lastIdx + 1) / 12), ((lastIdx + 1) % 12) + 1)
}

// Find when (which month) all active obligations are done. Same idea
// but applied to monthly obligations' end_date.
export function findAllObligationsClearedMonth(obligations) {
  const monthly = obligations.filter(
    (o) => o.status === 'active' && o.frequency === 'monthly' && o.end_date,
  )
  if (monthly.length === 0) return null
  const lastEndIdx = Math.max(
    ...monthly.map((o) => monthIndex(dateToMonthKey(o.end_date))),
  )
  if (!Number.isFinite(lastEndIdx)) return null
  return monthKey(Math.floor((lastEndIdx + 1) / 12), ((lastEndIdx + 1) % 12) + 1)
}

// Exported for unit tests / debug.
export const __testing = {
  monthIndex,
  addMonths,
  installmentForMonth,
  obligationForMonth,
}
